import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box, Typography, IconButton, Tooltip,
  CircularProgress,
  TextField, InputAdornment,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ProjectPaginator, { PM_PAGE_SIZE_OPTIONS } from './ProjectPaginator'
import { useApp, useAppActions } from '../AppContext'
import { addToast } from '../services/Toasts'
import { pmInit, pmAddProject, pmDelProject } from '../services/ProjectManagerService'
import { FS_SM, FS_XL, ICON_MD, ICON_SM } from '../common/theme'
import InputModal from '../tabs/modals/InputModal'
import ConfirmModal from '../tabs/modals/ConfirmModal'
import CfpThreeDotsMenu from './CfpThreeDotsMenu'
import StateDisplay from './StateDisplay'
import { DataTable, type DataTableColumn } from './DataTable'
import FolderChip from './FolderChip'

export interface BidProjectListProps {
  onSelectProject: (p: any) => void
  // Toolbar bits - every consumer can turn them off independently.
  showToolbar?: boolean
  showAddBid?: boolean
  showSearch?: boolean
  showRefresh?: boolean
  showPaginator?: boolean
  // Per-row controls.
  showDelete?: boolean             // three-dots delete menu
  // Optional title for the toolbar ("Bids" by default).
  title?: string
  // testid prefix so callers (modal/page) can disambiguate.
  testIdPrefix?: string
}

// Self-contained bid list - toolbar + DataTable. Used by BidManagerTab and
// the project picker modal (BidProjectPickerModal). All paging / search /
// sort state lives here so consumers don't have to re-implement it.
export default function BidProjectList({
  onSelectProject,
  showToolbar = true,
  showAddBid = true,
  showSearch = true,
  showRefresh = true,
  showPaginator = true,
  showDelete = true,
  title = 'Bids',
  testIdPrefix = 'bm',
}: BidProjectListProps) {

  const { projects } = useApp()
  const { setProjects } = useAppActions()

  const [isLoadingPm, setIsLoadingPm] = useState(false)

  const [sortCol, setSortCol] = useState('name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  // Server-side pagination + search.
  const [pmPage, setPmPage] = useState(0)
  const [pmTotal, setPmTotal] = useState(0)
  const pmPageSizeRef = useRef(PM_PAGE_SIZE_OPTIONS[0])
  const [searchName, setSearchName] = useState('')
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [addBidOpen, setAddBidOpen] = useState(false)
  // Pending delete - holds the project the user clicked Delete on. The
  // ConfirmModal opens while non-null; null on confirm/cancel.
  const [pendingDelete, setPendingDelete] = useState<any | null>(null)

  const loadProjects = useCallback(async (page = 0, order = 'name', asc = true, searchname = '') => {
    setIsLoadingPm(true)
    try {
      const result = await pmInit(page, order, asc, pmPageSizeRef.current, searchname)
      setProjects(result?.projects ?? result?.items ?? [])
      setPmTotal(result?.projectcount ?? 0)
    } catch {
      addToast('error', 'Failed to load bids.')
    } finally {
      setIsLoadingPm(false)
    }
  }, [setProjects, addToast])

  useEffect(() => { loadProjects() }, [])

  const handleSearchChange = (value: string) => {
    setSearchName(value)
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    searchTimerRef.current = setTimeout(() => {
      setPmPage(0)
      loadProjects(0, sortCol, sortDir === 'asc', value)
    }, 400)
  }

  const handlePmPageChange = (newPage: number) => {
    setPmPage(newPage)
    loadProjects(newPage, sortCol, sortDir === 'asc', searchName)
  }

  const handlePageSizeChange = (newSize: number) => {
    pmPageSizeRef.current = newSize
    setPmPage(0)
    loadProjects(0, sortCol, sortDir === 'asc', searchName)
  }

  const handleAddProject = async (name: string) => {
    if (!name.trim()) return
    try {
      await pmAddProject(name.trim())
      setPmPage(0)
      loadProjects(0, sortCol, sortDir === 'asc', searchName)
    } catch {
      addToast('error', 'Failed to add bid.')
    }
  }

  const handleDeleteProject = async (p: any) => {
    try {
      await pmDelProject(p.projectsha)
      setPmPage(0)
      loadProjects(0, sortCol, sortDir === 'asc', searchName)
    } catch {
      addToast('error', 'Failed to delete bid.')
    }
  }

  // ── Columns ──────────────────────────────────────────────────────────────
  // Project name renders as a FolderChip in its read-only (borderless)
  // form, which folds the gold-folder icon into the same column.
  const cols: DataTableColumn<any>[] = [
    {
      id: 'name', label: 'Name', width: 240, minWidth: 100,
      render: (p) => (
        <FolderChip
          name={p.name}
          isProject
          testId={`${testIdPrefix}_project_chip`}
        />
      ),
    },
    {
      id: 'createdate', label: 'Created', width: 100, minWidth: 60, align: 'center',
      render: (p) => (
        <Typography variant="caption" sx={{ fontSize: '0.68rem', opacity: 0.5 }}>
          {p.createdate ? String(p.createdate).slice(0, 10) : ''}
        </Typography>
      ),
    },
    {
      id: 'status', label: 'Status', flex: 1, width: 0, minWidth: 100, sortable: false, align: 'right',
      render: (p) =>
        <>
          <StateDisplay questions={p.questions ?? []} />
          {showDelete &&
            <Box onClick={(e: any) => e.stopPropagation()} sx={{ ml: 1 }}>
              <CfpThreeDotsMenu
                items={[{
                  label: 'Delete Bid',
                  icon: <DeleteOutlineIcon sx={{ fontSize: ICON_SM }} />,
                  danger: true,
                  onClick: () => setPendingDelete(p),
                }]}
              />
            </Box>
          }
        </>,
    },

  ]

  return (
    <Box data-testid={`${testIdPrefix}_root`} sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {showAddBid && (
        <InputModal
          open={addBidOpen}
          title="Add Bid"
          okLabel="Add"
          placeholder="Bid name"
          singleLine
          testIdPrefix={`${testIdPrefix}_addbid`}
          onConfirm={handleAddProject}
          onClose={() => setAddBidOpen(false)}
        />
      )}

      {showDelete && (
        <ConfirmModal
          open={!!pendingDelete}
          title="Delete Bid"
          body={pendingDelete ? `Are you sure you want to delete "${pendingDelete.name}"? This cannot be undone.` : undefined}
          okLabel="Delete"
          testIdPrefix={`${testIdPrefix}_delbid_confirm`}
          onConfirm={() => { if (pendingDelete) handleDeleteProject(pendingDelete) }}
          onClose={() => setPendingDelete(null)}
        />
      )}

      {showToolbar && (
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7, mr: 0.25 }}>{title}</Typography>
          {showRefresh && (
            <Tooltip title="Refresh">
              <IconButton
                data-testid={`${testIdPrefix}_refresh_btn`}
                size="small"
                onClick={() => loadProjects(pmPage, sortCol, sortDir === 'asc', searchName)}
                disabled={isLoadingPm}
              >
                {isLoadingPm ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: ICON_MD }} />}
              </IconButton>
            </Tooltip>
          )}
          {showAddBid && (
            <Tooltip title="Add Bid">
              <IconButton data-testid={`${testIdPrefix}_add_btn`} size="small" onClick={() => setAddBidOpen(true)}>
                <AddIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </Tooltip>
          )}
          {showSearch && (
            <TextField
              size="small"
              placeholder="Search bids..."
              value={searchName}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: ICON_SM, opacity: 0.5 }} />
                  </InputAdornment>
                ),
                endAdornment: searchName ? (
                  <InputAdornment position="end">
                    <IconButton data-testid={`${testIdPrefix}_search_clear_btn`} size="small" onClick={() => handleSearchChange('')} sx={{ p: 0.25 }}>
                      <CloseIcon sx={{ fontSize: FS_XL }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              inputProps={{ 'data-testid': `${testIdPrefix}_search_input` }}
              sx={{ width: 180, '& .MuiInputBase-root': { fontSize: FS_SM, height: 28 } }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {showPaginator && (
            <ProjectPaginator
              projectcount={pmTotal}
              page={pmPage}
              pageSize={pmPageSizeRef.current}
              onPage={handlePmPageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </Box>
      )}

      {/* List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        <DataTable
          rows={projects}
          cols={cols}
          rowKey={(p) => p.projectsha}
          rowTestId={`${testIdPrefix}_project_row`}
          rowDataAttrs={(p) => ({ 'data-projectsha': p.projectsha })}
          onRowClick={onSelectProject}
          hoverHighlight
          sort={{ colId: sortCol, dir: sortDir }}
          onSortChange={(colId, dir) => {
            setSortCol(colId)
            setSortDir(dir)
            setPmPage(0)
            loadProjects(0, colId, dir === 'asc', searchName)
          }}
          loading={isLoadingPm}
          testIdPrefix={`${testIdPrefix}_project_table`}
          emptyContent={
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.45 }}>
                {searchName ? 'No bids match your search.' : 'No bids yet. Add one to get started.'}
              </Typography>
            </Box>
          }
        />
      </Box>
    </Box>
  )
}
